// src/socket.js
import {
  secondaryHeartbeatThrottled,
  startSecondaryPoll,
  stopSecondaryPoll,
} from "./backupHeartbeat";

import io from "socket.io-client";
// Tracks whether we're in an outage ("down cycle")
let downCycle = false;

const mockSocketClient = onSocketConnectedStatus => {
  if (typeof onSocketConnectedStatus === "function") {
    onSocketConnectedStatus(true);
  }

  const noop = () => null;
  return {
    subscribeToQueryReturnsForUser: noop,
    subscribeToDispatchForUser: noop,
    unsubscribeToDispatchForUser: noop,
    subscribeToIncident: noop,
    unsubscribeToIncident: noop,
    subscribeToOfficerLocation: noop,
    unsubscribeFromOfficerLocation: noop,
    subscribeToNarrativeSegment: noop,
    unsubscribeFromNarrativeSegment: noop,
    socket: {
      connect: noop,
      disconnect: noop,
      on: noop,
      off: noop,
    },
    manualReconnect: noop,
  };
};

function beginDownCycle(s, opts) {
  const doImmediateCheck = opts && opts.doImmediateCheck;
  if (downCycle) return; // already handling this outage
  downCycle = true;

  // OPTIONAL: one immediate connectivity check at the start
  if (doImmediateCheck) {
    (async () => {
      try {
        const ok = await secondaryHeartbeatThrottled(4000);
        try {
          window.dispatchEvent(new Event(ok ? "online" : "offline"));
        } catch (_) {
          // Event dispatch is best effort.
        }
      } catch (_) {
        // The secondary heartbeat will retry on the next poll.
      }
    })();
  }

  // Keep polling (singleton) until socket reconnects
  startSecondaryPoll(5000, () => {
    // onOnline: nudge a reconnect when internet is back
    try {
      s.connect();
    } catch (_) {
      // Reconnection is best effort.
    }
  });
}

function endDownCycle() {
  if (!downCycle) return;
  downCycle = false;
  stopSecondaryPoll(); // stop only when socket is truly back
}

export const socket = onSocketConnectedStatus => {
  const env = import.meta.env;
  if (
    env.VITE_MOCK_SOCKET === "true" ||
    (typeof window !== "undefined" && window.Cypress)
  ) {
    return mockSocketClient(onSocketConnectedStatus);
  }

  const protocol = env.VITE_PROTOCOL;
  const host = env.VITE_SYNC_HOST;
  const port = env.VITE_PORT;
  const url = `${protocol}://${host}:${port}`;
  const s = io.connect(url);

  const notify = isConnected => {
    if (typeof onSocketConnectedStatus === "function") {
      try {
        onSocketConnectedStatus(!!isConnected);
      } catch (_) {
        // Status notifications must not interrupt socket handling.
      }
    }
  };

  // ---- lifecycle ----
  s.on("connect", () => {
    console.log("socket connect");
    notify(true);
    endDownCycle();
    try {
      window.dispatchEvent(new Event("online"));
    } catch (_) {
      // Event dispatch is best effort.
    }
  });

  s.on("reconnect", () => {
    console.log("socket reconnect");
    notify(true);
    endDownCycle();
    try {
      window.dispatchEvent(new Event("online"));
    } catch (_) {
      // Event dispatch is best effort.
    }
  });

  s.on("disconnect", () => {
    console.log("socket disconnect");
    notify(false);
    beginDownCycle(s, { doImmediateCheck: true }); // one immediate check, then poll
  });

  s.on("connect_error", () => {
    console.log("socket connect_error");
    notify(false);
    beginDownCycle(s, { doImmediateCheck: false }); // don't spam checks on retries
  });

  // optional cosmetics
  s.on("pong", function () {
    try {
      window.dispatchEvent(new Event("online"));
    } catch (_) {
      // Event dispatch is best effort.
    }
  });

  // ---- your subscription API (unchanged) ----
  function subscribeToQueryReturnsForUser(officerId, handler) {
    s.on(`${officerId}:queryReturns`, handler);
    return null;
  }
  function subscribeToDispatchForUser(officerId, handler) {
    s.on(`${officerId}:dispatched`, handler);
    return null;
  }
  function unsubscribeToDispatchForUser(officerId) {
    s.off(`${officerId}:dispatched`);
    return null;
  }
  function subscribeToIncident(incidentId, handler) {
    s.on(`${incidentId}:incidentDetail`, handler);
    return null;
  }
  function unsubscribeToIncident(incidentId) {
    s.off(`${incidentId}:incidentDetail`);
    return null;
  }
  function subscribeToOfficerLocation(officerId, handler) {
    s.on(`location:${officerId}:officer_id`, handler);
    return null;
  }
  function unsubscribeFromOfficerLocation(officerId) {
    s.off(`location:${officerId}:officer_id`);
    return null;
  }
  function subscribeToNarrativeSegment(narrativeSegmentId, handler) {
    s.on(`${narrativeSegmentId}:narrative`, handler);
    return null;
  }
  function unsubscribeFromNarrativeSegment(narrativeSegmentId) {
    s.off(`${narrativeSegmentId}:narrative`);
    return null;
  }
  function manualReconnect() {
    try {
      s.connect();
    } catch (_) {
      // Reconnection is best effort.
    }
  }

  return {
    subscribeToQueryReturnsForUser,
    subscribeToDispatchForUser,
    unsubscribeToDispatchForUser,
    subscribeToIncident,
    unsubscribeToIncident,
    subscribeToOfficerLocation,
    unsubscribeFromOfficerLocation,
    subscribeToNarrativeSegment,
    unsubscribeFromNarrativeSegment,
    socket: s,
    manualReconnect,
  };
};
